'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { I18nextProvider } from 'react-i18next';
import { type ReactNode, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { AUTH_SESSION_QUERY_KEY } from '../features/auth/constants';
import { readStoredSession } from '../features/auth/lib/authSessionStorage';
import {
  AnalyticsConsentBanner,
  AnalyticsConsentProvider,
  AnalyticsRouteListener,
  GoogleAnalytics,
} from '../components/analytics';
import { bindApiAuthQueryClient } from '../services/api';
import { applyThemeFromConfig } from '../theme/apply-theme';
import { i18n } from '../i18n';
import { normalizeMatchLocale } from '../i18n/matchLocale';

export function AppProviders({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? '';
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: 1,
          staleTime: 60_000,
          gcTime: 1_800_000,
        },
        mutations: {
          retry: 0,
        },
      },
    });
    const session = readStoredSession();
    if (session) {
      client.setQueryData(AUTH_SESSION_QUERY_KEY, session);
    }
    bindApiAuthQueryClient(client);
    return client;
  });

  useEffect(() => {
    applyThemeFromConfig();
  }, []);

  useEffect(() => {
    const syncDocumentLanguage = (language: string) => {
      const isLocalizedPublicMatchRoute =
        pathname === '/adopt' ||
        pathname.startsWith('/adopt/') ||
        pathname.startsWith('/match/');
      document.documentElement.lang = isLocalizedPublicMatchRoute
        ? normalizeMatchLocale(language)
        : 'en';
    };
    syncDocumentLanguage(i18n.resolvedLanguage ?? i18n.language);
    i18n.on('languageChanged', syncDocumentLanguage);
    return () => {
      i18n.off('languageChanged', syncDocumentLanguage);
    };
  }, [pathname]);

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <AnalyticsConsentProvider>
          <GoogleAnalytics />
          <AnalyticsRouteListener />
          {children}
          <AnalyticsConsentBanner />
        </AnalyticsConsentProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </I18nextProvider>
  );
}
